<script setup lang="ts">
import { onMounted, onUnmounted, ref, reactive, nextTick, computed } from "vue";
import OT from "@opentok/client";

import type { streamsStatus, DevicesInfo, VideoCallConfig } from "../definitions";
import {
  PARTICIPANT,
  VIDEO_CALL_EXCEPTIONS,
  VIDEO_CALL_STATUS,
  TRACK_KIND,
  VIDEO_CALL_NETWORK_QUALITY,
} from "../definitions";
import { VIDEO_CALL_COMPLETE_REASON } from "@/Modules/Models/Api/VideoCalls";
import { config } from "@/Common/envConfig";

import useUserStore from "@/Modules/User/Store";
import useVideoCallStore from "@/Modules/VideoCall/Store";
import useAppStore from "@/Modules/App/Store";
import useAgentStore from "@/Modules/Agent/Store";

import Participant from "./Participant.vue";
import VideoActions from "./Actions";
import Notifications from "./Notifications.vue";
import CounterTime from "./Counter.vue";
import CallerHeader from "./CallerHeader.vue";
import CallHandler from "./CallHandler.vue";
import TranscriptionBanner from "./TranscriptionBanner.vue";

import { log as logger } from "@/Utils/Debug";
import { isIosApp } from "@/Utils/Ionic";
import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { stopRecorder } from "./utils/RecordAudio";

import { MIN_SCALE } from "../constants";

export interface CallSessionProps {
  sessionId: string;
  token: string;
  scale?: number;
  transcription?: boolean;
}

const DEFAULT_STREAMS_STATUS: streamsStatus = {
  [PARTICIPANT.LOCAL]: {
    [TRACK_KIND.VIDEO]: true,
    [TRACK_KIND.AUDIO]: true,
    isUserVideoAction: false,
    isUserAudioAction: false,
  },
  [PARTICIPANT.REMOTE]: {
    [TRACK_KIND.VIDEO]: false,
    [TRACK_KIND.AUDIO]: false,
    isUserVideoAction: false,
    isUserAudioAction: false,
  },
  [PARTICIPANT.SHARE_SCREEN]: {
    [TRACK_KIND.VIDEO]: false,
    [TRACK_KIND.AUDIO]: false,
    isUserVideoAction: false,
    isUserAudioAction: false,
  },
};

const userStore = useUserStore();
const videoCallStore = useVideoCallStore();
const appStore = useAppStore();
const agentStore = useAgentStore();

const props = withDefaults(defineProps<CallSessionProps>(), {
  scale: MIN_SCALE,
  transcription: false,
});

const emit = defineEmits<{
  (event: "rejectCall", reason: VIDEO_CALL_COMPLETE_REASON): void; // Only fired on waiting status
  (event: "endCall", reason?: VIDEO_CALL_COMPLETE_REASON): void; // Only end call in calling status
  (event: "showChat"): void;
  (event: "streamSubscribed"): void;
  (event: "publisherReady"): void;
  (event: "error", value: { code: VIDEO_CALL_EXCEPTIONS; exception?: unknown }): void;
  (event: "selectParticipant", value: PARTICIPANT): void;
  (event: "updateScale", value: number): void;
}>();

const isReadySession = ref<boolean>(false);
const isAccepting = ref<boolean>(false);
const isRejecting = ref<boolean>(false);
const hasRemoteStream = ref<boolean>(false);
const hideElements = ref<boolean>(false);
const streams = reactive<streamsStatus>({ ...DEFAULT_STREAMS_STATUS });
const selectedParticipant = ref<PARTICIPANT>(PARTICIPANT.REMOTE);
const sessionRef = ref<HTMLElement>();
const isReadyShareScreen = ref<boolean>(false);
const networkQuality = ref<VIDEO_CALL_NETWORK_QUALITY>(VIDEO_CALL_NETWORK_QUALITY.HIGHEST);
const selectedDevices = reactive<DevicesInfo>({
  audio: undefined,
  video: undefined,
});
const showTranscripcionBanner = ref<boolean>(false);
const hasBlur = ref<boolean>(false);
const remoteVideoConfig = ref<VideoCallConfig | undefined>(undefined);

let session: OT.Session | undefined = undefined;
let subscriberStream: OT.Stream | undefined = undefined;
let shareScreenStream: OT.Stream | undefined = undefined;

const log = (...args: unknown[]) => {
  logger(`[SESSION]`, ...args);
};

const remoteUsername = computed((): string => {
  if (hasValue(videoCallStore.remoteUser) && isValidString(videoCallStore.remoteUser.username)) {
    return videoCallStore.remoteUser!.username;
  }

  return "";
});

const canConnectToVideoCall = computed((): boolean => {
  if (userStore.getUserIsTherapist) {
    return true;
  } else if (videoCallStore.isInCall) {
    return true;
  } else {
    return isAccepting.value || !!streams.REMOTE.video || !!streams.REMOTE.audio;
  }
});

const errorHandler = ({ code, exception }: { code: VIDEO_CALL_EXCEPTIONS; exception?: unknown }) => {
  emit("error", { code, exception });
};

const handlePublished = () => {
  log("PUBLISHED");

  videoCallStore.stopLoadingVideocall();
  selectedParticipant.value = PARTICIPANT.REMOTE;

  if (!videoCallStore.isInCall) {
    emit("publisherReady");

    if (videoCallStore.showConfig && userStore.getUserIsPatient) {
      nextTick(() => handleAcceptCall());
    }
  }
};

const handleUnpublish = (part: PARTICIPANT) => {
  if (part === PARTICIPANT.LOCAL) {
    session?.disconnect();

    emit("endCall", VIDEO_CALL_COMPLETE_REASON.OK);
  }
};

const addSessionHandlers = () => {
  if (hasValue(session)) {
    session.on("streamCreated", (event): void => {
      log("STREAM CREATEED", event, subscriberStream);

      if (!hasValue(subscriberStream)) {
        log("SUBSCRIBING TO STREAM");
        subscriberStream = event.stream;
        hasRemoteStream.value = true;
        handleChangeTrack(PARTICIPANT.REMOTE, TRACK_KIND.VIDEO, true);
        handleChangeTrack(PARTICIPANT.REMOTE, TRACK_KIND.AUDIO, true);
      } else if (event.stream.videoType === "screen") {
        log("SUBSCRIBING TO SHARE SCREEN");

        shareScreenStream = event.stream;
        handleChangeTrack(PARTICIPANT.SHARE_SCREEN, TRACK_KIND.VIDEO, true);
      }
    });

    session.on("sessionDisconnected", (event) => {
      // The event is defined by the SessionDisconnectEvent class
      log("Disconnected from the session.", event);
    });

    session.on("sessionReconnecting", (event) => {
      // The event is defined by the SessionDisconnectEvent class
      log("RECONECTING from the session.", event);
    });

    session.on("sessionReconnected", (event) => {
      // The event is defined by the SessionDisconnectEvent class
      log("RECONNECTED from the session.", event);
    });

    session.on("connectionCreated", (event) => {
      log("CONNECTION CREATED", event);
    });

    session.on("connectionDestroyed", (event) => {
      log("CONNECTION DESTROYED", event);

      videoCallStore.updateStatus(VIDEO_CALL_STATUS.CLOSING);
    });

    session.on("streamDestroyed", (event) => {
      log("Destroy: event", event, subscriberStream);

      if (event.stream.streamId === subscriberStream?.streamId) {
        emit(
          "endCall",
          event.reason === "networkDisconnected" ? VIDEO_CALL_COMPLETE_REASON.CONNECTION : undefined
        );
      } else if (
        isValidString(shareScreenStream?.streamId) &&
        event.stream.streamId === shareScreenStream?.streamId
      ) {
        handleStopShareScreen();
      }
    });

    session?.on("signal:config", ({ data }) => {
      if (hasValue(data)) {
        log("Received config", data);

        const conf = JSON.parse(data) as { userId: string } & VideoCallConfig;

        if (conf.userId !== userStore.user?.id) {
          log("Remote config saved", conf);
          remoteVideoConfig.value = conf;
        }
      }
    });
  }
};

const handleStreamSubscribed = () => {
  nextTick().then(() => {
    streams.REMOTE.video = true;
    streams.REMOTE.audio = true;

    isAccepting.value = false;

    emit("streamSubscribed");
  });
};

const handleEndVideoCall = () => {
  videoCallStore.updateStatus(VIDEO_CALL_STATUS.UNPUBLISHING);
};

const handleRejectVideoCall = () => {
  isRejecting.value = true;

  if (userStore.getUserIsTherapist) {
    emit("rejectCall", VIDEO_CALL_COMPLETE_REASON.MISSED);
  } else {
    emit("rejectCall", VIDEO_CALL_COMPLETE_REASON.REJECTED);
  }
};

const handleAcceptCall = () => {
  isAccepting.value = true;

  videoCallStore.hangUpVideoCall();
};

const adjustSecondaryParticipants = () => {
  const display = appStore.getAppDisplay();
  const secondaryParts = sessionRef.value!.getElementsByClassName("session__participant--secondary");
  const spacer = 16;
  const counter = document.getElementById("video_call_counter");

  let acumulatedHeight = display.top;

  if (videoCallStore.isTranscriptionEnabled) {
    if (showTranscripcionBanner.value) {
      if (isIosApp()) {
        acumulatedHeight += 124;
      } else if (appStore.isMobileViewport) {
        acumulatedHeight += 84;
      }

      acumulatedHeight += 52;
    }

    if (hasValue(counter)) {
      if (hideElements.value) {
        counter.style.top = "";
      } else {
        counter.style.top = `${acumulatedHeight + spacer}px`;
      }
    }
  }

  for (let i = 0; i < secondaryParts.length; i++) {
    const part = secondaryParts[i] as HTMLElement;

    part.style.top = `${acumulatedHeight + spacer}px`;
    acumulatedHeight += spacer + part.clientHeight;
  }
};

const handleSelectParticipant = (event?: Event) => (part: PARTICIPANT) => {
  if (!videoCallStore.isInCall) return;

  if (hasValue(event)) {
    event.stopPropagation();
    event.preventDefault();
  }

  if (part !== selectedParticipant.value) {
    log("Select participant", part);

    selectedParticipant.value = part;

    nextTick(adjustSecondaryParticipants);

    if (videoCallStore.isInCall) {
      emit("updateScale", MIN_SCALE);
    }
  }
};

const handleShareScreenSubscribed = () => {
  isReadyShareScreen.value = true;
  handleSelectParticipant()(PARTICIPANT.SHARE_SCREEN);
};

/* STREAMS MANAGEMENT */

const handleCloseNotification = (track: TRACK_KIND) => {
  if (track === TRACK_KIND.VIDEO) {
    streams[PARTICIPANT.LOCAL].isUserVideoAction = false;
  } else {
    streams[PARTICIPANT.LOCAL].isUserAudioAction = false;
  }
};

const handleChangeTrack = (part: PARTICIPANT, track: TRACK_KIND, enabled = false, isUserAction = true) => {
  log("Change track", part, track, enabled);

  streams[part][track] = enabled;

  if (isUserAction && part === PARTICIPANT.LOCAL) {
    if (track === TRACK_KIND.VIDEO) {
      streams[part].isUserVideoAction = true;
    } else {
      streams[part].isUserAudioAction = true;
    }
  }
};

const handleStopShareScreen = () => {
  if (isReadyShareScreen.value || streams.SHARE_SCREEN.video) {
    handleChangeTrack(PARTICIPANT.SHARE_SCREEN, TRACK_KIND.VIDEO, false);
    handleSelectParticipant()(PARTICIPANT.REMOTE);
    isReadyShareScreen.value = false;
  }
};

const handleUnsopportedShareScreen = () => {
  streams.SHARE_SCREEN.video = undefined;
};

const resetStreams = () => {
  const parts = [PARTICIPANT.LOCAL, PARTICIPANT.REMOTE, PARTICIPANT.SHARE_SCREEN];

  parts.forEach((p) => {
    streams[p] = DEFAULT_STREAMS_STATUS[p];
  });
};

const handleForceStopVideo = () => {
  log("Force stop video");
  networkQuality.value = VIDEO_CALL_NETWORK_QUALITY.LOWEST;

  nextTick(() => {
    streams.LOCAL.video = false;
  });
};

const handleForceEnableVideo = () => {
  log("Force enable video");

  networkQuality.value = VIDEO_CALL_NETWORK_QUALITY.HIGHEST;

  nextTick(() => {
    streams.LOCAL.video = true;
  });
};

const handleChangeStreamQuality = (quality: VIDEO_CALL_NETWORK_QUALITY) => {
  log("Change stream quality to", quality);

  networkQuality.value = quality;
};

/* STREAMS MANAGEMENT */

/* DEVICES MANAGEMENT */

const handleSelectAudioDevice = (device: OT.Device) => {
  if (videoCallStore.isInCall && selectedDevices.audio?.id !== device.deviceId) {
    selectedDevices.audio = {
      id: device.deviceId,
      label: device.label,
    };
  } else if (!isValidString(device?.deviceId)) {
    selectedDevices.audio = undefined;
  }
};

const handleSelectVideoDevice = (device: OT.Device) => {
  if (videoCallStore.isInCall && selectedDevices.video?.id !== device.deviceId) {
    selectedDevices.video = {
      id: device.deviceId,
      label: device.label,
    };
  } else if (!isValidString(device?.deviceId)) {
    selectedDevices.video = undefined;
  }
};
/* DEVICES MANAGEMENT */

/* TRANSCRIPTION */

const toggleTranscription = () => {
  showTranscripcionBanner.value = !showTranscripcionBanner.value;
  adjustSecondaryParticipants();
};

/* TRANSCRIPTION */

const handleHiddenContent = () => {
  if (videoCallStore.status === VIDEO_CALL_STATUS.CALLING) {
    hideElements.value = !hideElements.value;

    if (showTranscripcionBanner.value) {
      toggleTranscription();
    }
  }
};

const handleBot = () => {
  agentStore.toggleBotChat();
};

const handleToggleBlur = (value: boolean | undefined) => {
  if (hasValue(value)) {
    hasBlur.value = value;
  } else {
    hasBlur.value = !hasBlur.value;
  }
};

onMounted(() => {
  resetStreams();
  log("Init video call session", props.sessionId);

  subscriberStream = undefined;

  isReadySession.value = false;
  hideElements.value = false;
  isRejecting.value = false;
  isAccepting.value = false;
  hasBlur.value = isValidString(videoCallStore.getConfig()?.blur);

  session = OT.initSession(config.vonageApiKey!, props.sessionId);

  addSessionHandlers();

  isReadySession.value = true;
});

onUnmounted(() => {
  log("UNMOUNTED SESSION");

  if (hasValue(session)) {
    session.disconnect();
  }

  stopRecorder();
});
</script>

<template>
  <div class="session" ref="sessionRef">
    <div class="session__cover" />

    <TranscriptionBanner
      v-if="showTranscripcionBanner"
      class="session__transcription_banner"
      @close="toggleTranscription"
    />

    <CallerHeader
      v-if="userStore.getUserIsPatient && hasValue(videoCallStore.remoteUser) && !videoCallStore.isInCall"
      class="session__caller_header"
      :user="videoCallStore.remoteUser"
    />

    <CallHandler
      v-if="!videoCallStore.isInCall && !videoCallStore.showConfig"
      class="session__handler"
      :isLoading="isAccepting || isRejecting"
      @onHangUp="handleAcceptCall"
      @onHangDown="handleRejectVideoCall"
    />

    <CounterTime
      v-if="videoCallStore.isInCall"
      class="session__counter_time"
      :class="{
        'session__counter_time--hide': hideElements,
        'session__counter_time--show_notification': showTranscripcionBanner,
      }"
      @clickTranscription="toggleTranscription"
    />

    <template v-if="isReadySession">
      <Notifications
        :streamsStatus="streams"
        :remoteName="remoteUsername"
        :networkQuality="networkQuality"
        @closeAction="handleCloseNotification"
      />

      <Participant
        class="session__participant session__participant--local"
        :class="{
          'session__participant--selected': selectedParticipant === PARTICIPANT.LOCAL,
          'session__participant--secondary': selectedParticipant !== PARTICIPANT.LOCAL,
          'hide-mobile-flex': streams.SHARE_SCREEN.video,
        }"
        :session="session!"
        :canConnect="canConnectToVideoCall"
        :token="props.token"
        :user="userStore.user!"
        :trackStatus="streams.LOCAL"
        :participant="PARTICIPANT.LOCAL"
        :transcription="props.transcription"
        :networkQuality="networkQuality"
        :devices="selectedDevices"
        :scale="selectedParticipant === PARTICIPANT.LOCAL ? scale : MIN_SCALE"
        :blur="hasBlur"
        :videoConfig="videoCallStore.getConfig()"
        @stopTrack="(t: TRACK_KIND) => handleChangeTrack(PARTICIPANT.LOCAL, t, false, true)"
        @error="errorHandler"
        @published="handlePublished"
        @publisherReady="handlePublished"
        @updateDimensions="adjustSecondaryParticipants"
        @click="(ev: Event) => handleSelectParticipant(ev)(PARTICIPANT.LOCAL)"
        @forceStopVideo="handleForceStopVideo"
        @forceEnableVideo="handleForceEnableVideo"
        @unpublish="handleUnpublish"
      />

      <div
        class="session__participant session__participant__cover"
        v-if="!hasRemoteStream || !canConnectToVideoCall || !videoCallStore.isInCall"
      />

      <Participant
        v-if="hasRemoteStream && canConnectToVideoCall"
        class="session__participant session__participant--remote"
        :class="{
          'session__participant--selected': selectedParticipant === PARTICIPANT.REMOTE,
          'session__participant--secondary': selectedParticipant !== PARTICIPANT.REMOTE,
        }"
        :stream="subscriberStream"
        :session="session!"
        :user="videoCallStore.remoteUser!"
        :trackStatus="streams.REMOTE"
        :participant="PARTICIPANT.REMOTE"
        :networkQuality="networkQuality"
        :scale="selectedParticipant === PARTICIPANT.REMOTE ? scale : MIN_SCALE"
        :videoConfig="remoteVideoConfig"
        @streamSubscribed="handleStreamSubscribed"
        @error="errorHandler"
        @click="(ev: Event) => handleSelectParticipant(ev)(PARTICIPANT.REMOTE)"
        @updateDimensions="adjustSecondaryParticipants"
        @trackStopped="(t: TRACK_KIND) => handleChangeTrack(PARTICIPANT.REMOTE, t, false, false)"
        @trackEnabled="(t: TRACK_KIND) => handleChangeTrack(PARTICIPANT.REMOTE, t, true, false)"
        @changeStreamQuality="handleChangeStreamQuality"
      />

      <Participant
        v-if="streams.SHARE_SCREEN.video"
        v-show="
          videoCallStore.status === VIDEO_CALL_STATUS.CALLING &&
          (userStore.getUserIsTherapist || isReadyShareScreen)
        "
        class="session__participant session__participant--share_screen"
        :class="{
          'session__participant--selected': selectedParticipant === PARTICIPANT.SHARE_SCREEN,
          'session__participant--secondary': selectedParticipant !== PARTICIPANT.SHARE_SCREEN,
        }"
        :session="session!"
        :stream="shareScreenStream"
        :trackStatus="streams.SHARE_SCREEN"
        :participant="PARTICIPANT.SHARE_SCREEN"
        :networkQuality="networkQuality"
        :scale="selectedParticipant === PARTICIPANT.SHARE_SCREEN ? scale : MIN_SCALE"
        @error="errorHandler"
        @updateDimensions="adjustSecondaryParticipants"
        @published="handleSelectParticipant()(PARTICIPANT.SHARE_SCREEN)"
        @streamSubscribed="handleShareScreenSubscribed"
        @streamDestroy="handleStopShareScreen"
        @click="(ev: Event) => handleSelectParticipant(ev)(PARTICIPANT.SHARE_SCREEN)"
      />

      <VideoActions
        v-if="videoCallStore.isInCall"
        class="session__actions"
        :class="{ 'session__actions--hide': hideElements }"
        :streamsStatus="streams"
        :networkQuality="networkQuality"
        :isBotEnabled="agentStore.isBotEnabled"
        :hideElements="hideElements"
        :hasBlur="hasBlur"
        @endVideoCall="handleEndVideoCall"
        @showChat="emit('showChat')"
        @startTrack="(t: TRACK_KIND) => handleChangeTrack(PARTICIPANT.LOCAL, t, true, true)"
        @stopTrack="(t: TRACK_KIND) => handleChangeTrack(PARTICIPANT.LOCAL, t, false, true)"
        @startShareScreen="handleChangeTrack(PARTICIPANT.SHARE_SCREEN, TRACK_KIND.VIDEO, true, true)"
        @stopShareScreen="handleStopShareScreen"
        @unsupportedShareScreen="handleUnsopportedShareScreen"
        @bot="handleBot"
        @clickOutside="handleHiddenContent"
        @selectAudioDevice="handleSelectAudioDevice"
        @selectVideoDevice="handleSelectVideoDevice"
        @toggleBlur="handleToggleBlur"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.session {
  &__transcription_banner {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 4;
  }

  &__counter_time {
    transition: top 1s ease-out;
    position: absolute;
    top: calc($space-4 + var(--ion-safe-area-top));
    left: $space-4;
    z-index: 3;

    &--hide {
      top: -200px;
    }

    &--show_notification {
      top: calc(52px + $space-4);
    }
  }

  &__cover {
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 2;
    background: linear-gradient(
      180deg,
      rgba(43, 43, 43, 0.147277) 5.44%,
      rgba(22, 22, 22, 0.0001) 51.8%,
      rgba(0, 0, 0, 0.250821) 97.3%
    );
  }

  &__participant {
    &--selected,
    &__cover {
      z-index: 1;

      height: 100% !important;
      width: 100% !important;
    }

    &--secondary {
      @include radius(m);
      @include elevation(m);

      max-height: 112px;
      max-width: 96px;
      position: absolute;
      top: 46px;
      right: 16px;
      cursor: pointer;
      z-index: 3;
      transition: top 1s ease-out;

      @media (min-width: $breakpoint-m) {
        max-height: 160px;
        max-width: 120px;
      }
    }

    &__cover {
      z-index: 2;
      background-image: url("@/Assets/images/logo_256.svg");
      background-color: white;
      background-position: center;
      background-repeat: no-repeat;
    }
  }

  &__caller_header {
    position: absolute;
    top: calc($space-10 + var(--ion-safe-area-top));
    left: $space-10;
    z-index: 2;
  }

  &__handler {
    @include sp.margin(b, 14, true);

    position: absolute;
    bottom: 0px;
    width: 100%;
    display: flex;
    z-index: 2;
  }

  &__actions {
    @include colors.bg(background, overlay);

    padding-bottom: calc($space-4 + var(--ion-safe-area-bottom));
    transition: bottom 1s ease-out;
    position: absolute;
    bottom: 0px;
    width: 100%;
    z-index: 2;

    &--hide {
      bottom: -200px;
    }
  }
}
</style>
